import { useState, useEffect } from 'react';

const CACHE_KEY = 'patreon_posts_cache';
const CACHE_TIMESTAMP_KEY = 'patreon_posts_cache_timestamp';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const usePatreonPosts = () => {
  const [posts, setPosts] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      // Check localStorage cache first
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp, 10);
        if (age < CACHE_TTL) {
          try {
            const parsed = JSON.parse(cachedData);
            setPosts(parsed.posts || []);
            setCampaign(parsed.campaign || null);
            setLoading(false);
            return;
          } catch (e) {
            // Invalid cache, continue to fetch
          }
        }
      }

      try {
        const response = await fetch('/.netlify/functions/patreon');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.message || data.error);
        }

        setPosts(data.posts || []);
        setCampaign(data.campaign || null);

        // Cache in localStorage
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (err) {
        console.error('Error fetching Patreon posts:', err);
        setError(err);

        // Try to use stale cache as fallback
        const staleCache = localStorage.getItem(CACHE_KEY);
        if (staleCache) {
          try {
            const parsed = JSON.parse(staleCache);
            setPosts(parsed.posts || []);
            setCampaign(parsed.campaign || null);
          } catch (e) {
            // Can't use cache
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return { posts, campaign, loading, error };
};

// Helper: Get public posts only
export const getPublicPosts = (posts) => {
  return posts.filter(post => post.isPublic);
};

// Helper: Get member-only posts
export const getMemberPosts = (posts) => {
  return posts.filter(post => !post.isPublic);
};

export default usePatreonPosts;
